import React, { createContext, useState, useContext, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
    en: {
        nav: {
            gallery: 'Gallery',
            services: 'Services',
            about: 'About',
            contact: 'Contact',
        },
        home: {
            hero: 'The Art of Cinematic Stillness',
            vision: 'The Vision',
            featured: 'Featured Selection',
            viewAll: 'View Full Gallery',
            narrative: 'Beyond the frame, there is a story. Through light, shadow, and silence, Alister Bryan captures moments that feel like a frame from a film you’ve always known.',
        },
        gallery: {
            all: 'All Work',
            allMedia: 'All Media',
            photos: 'Photos',
            videos: 'Videos',
            casamentos: 'Weddings',
            ensaios: 'Portraits',
            comercial: 'Commercial',
            cinema: 'Cinematic',
            street: 'Street',
            categories: 'Categories',
        },
        services: {
            title: 'Services',
            preTitle: 'The Packages',
            photography: 'Photography Packages',
            cinematography: 'Cinematography',
            enquire: 'Enquire Now',
            featuresPhoto: ['Full Day Coverage', 'Digital Delivery', 'Advanced Retouching', 'Print Rights'],
            featuresCinema: ['4K Cinematic Film', 'Pro Audio Recording', '2 Cinematographers', 'Drone Footage'],
            pricePhoto: 'from $2,500',
            priceCinema: 'from $4,000',
        },
        about: {
            title: 'Between the Moment and the Memory',
            bio: 'Alister Bryan works between documentary observation and cinematic direction, creating images with precision and room for the unexpected.',
            expertise: 'Creative territories',
            equipment: 'The edit',
            philosophyTitle: 'The point of view',
            philosophyDesc: 'Photography is about understanding the rhythm of a scene and choosing the instant when form and emotion meet.',
            processTitle: 'The direction',
            processDesc: 'Every project begins with listening. Intention, references, and context shape a visual language built for that particular story.',
            gearTitle: 'The delivery',
            gearDesc: 'The final work is shaped as a coherent sequence, with rhythm, contrast, and color carrying the story from the first frame to the last.',
        },
        contact: {
            title: 'Let’s Connect',
            name: 'Name',
            email: 'Email',
            message: 'Message',
            send: 'Send Inquiry',
            success: 'Message sent successfully. We will be in touch.',
        },
        lightbox: {
            close: 'Close',
            story: 'The Story',
            scrollForMore: 'Scroll for more',
        },
    },
    pt: {
        nav: {
            gallery: 'Galeria',
            services: 'Serviços',
            about: 'Sobre',
            contact: 'Contato',
        },
        home: {
            hero: 'A Arte da Estaticidade Cinematográfica',
            vision: 'A Visão',
            featured: 'Seleção em Destaque',
            viewAll: 'Ver Galeria Completa',
            narrative: 'Além do quadro, há uma história. Através de luz, sombra e silêncio, Alister Bryan captura momentos que parecem um frame de um filme que você sempre conheceu.',
        },
        gallery: {
            all: 'Tudo',
            allMedia: 'Todas as Mídias',
            photos: 'Fotos',
            videos: 'Vídeos',
            casamentos: 'Casamentos',
            ensaios: 'Ensaios',
            comercial: 'Comercial',
            cinema: 'Cinematográfico',
            street: 'Rua',
            categories: 'Categorias',
        },
        services: {
            title: 'Serviços',
            preTitle: 'Os Pacotes',
            photography: 'Pacotes de Fotografia',
            cinematography: 'Cinematografia',
            enquire: 'Solicitar Orçamento',
            featuresPhoto: ['Cobertura Integral', 'Entrega Digital', 'Edição Avançada', 'Direitos de Impressão'],
            featuresCinema: ['Filme Cinematográfico 4K', 'Áudio Profissional', '2 Cinegrafistas', 'Imagens Aéreas'],
            pricePhoto: 'a partir de $2,500',
            priceCinema: 'a partir de $4,000',
        },
        about: {
            title: 'Entre o Instante e a Memória',
            bio: 'Alister Bryan trabalha entre o olhar documental e a direção cinematográfica, criando imagens com precisão e espaço para o inesperado.',
            expertise: 'Territórios de criação',
            equipment: 'A edição',
            philosophyTitle: 'O ponto de vista',
            philosophyDesc: 'Fotografar é entender o ritmo da cena e escolher o instante em que forma e emoção se encontram.',
            processTitle: 'A direção',
            processDesc: 'Cada projeto começa com escuta. Intenção, referências e contexto definem uma linguagem visual construída para aquela história.',
            gearTitle: 'A entrega',
            gearDesc: 'O trabalho final é pensado como uma sequência coerente, em que ritmo, contraste e cor conduzem a narrativa do primeiro ao último quadro.',
        },
        contact: {
            title: 'Imortalize seu Momento',
            name: 'Nome',
            email: 'E-mail',
            message: 'Mensagem',
            send: 'Enviar Mensagem',
            success: 'Mensagem enviada com sucesso. Entraremos em contato.',
        },
        lightbox: {
            close: 'Fechar',
            story: 'A História',
            scrollForMore: 'Role para ver mais',
        },
    }
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(() => {
        if (typeof window !== 'undefined') {
            const savedLanguage = localStorage.getItem('language');
            if (savedLanguage) return savedLanguage;
            if (navigator.language?.toLowerCase().startsWith('pt')) return 'pt';
        }
        return 'en';
    });

    useEffect(() => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('language', language);
            document.documentElement.lang = language === 'pt' ? 'pt-BR' : 'en';
        }
    }, [language]);

    const toggleLanguage = () => {
        setLanguage(prev => (prev === 'pt' ? 'en' : 'pt'));
    };

    const t = (key) => {
        const keys = key.split('.');
        let value = translations[language];
        for (const k of keys) {
            if (value && value[k]) {
                value = value[k];
            } else {
                return key;
            }
        }
        return value;
    };

    return (
        <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => useContext(LanguageContext);
